import { useEffect, useRef, useState } from "react";
import { Search, Users } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Select } from "semantic-ui-react";
import { getBulkDropdownData } from "../../api/keyvalue";
import {
  addStakeholderGroupData,
  getStakeholderGroupDataById,
  searchStakeholderGroup,
  updateStakeholderGroupData,
} from "../../api/stakeholderGroupRequest";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import StakeholderGroupComponent from "../../components/master/StakeholderGroupComponent";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import { stakeholderGroupValidation } from "../../components/validations/StakeholderGroupCreation";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import { errorActions } from "../../store/error-slice";

const FORM = FORMS.STAKEHOLDER_GROUP.GRP.form;

const StakeholderGroup = () => {
  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.STAKEHOLDER_GROUP,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const dispatch = useDispatch();
  const errorState = useSelector((state) => state.error.stakeholderGroupErrors);
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: null,
    category: null,
    name: null,
    status: null,
    fromDate: null,
    toDate: null,
  });

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editStkGroup, setEditStkGroup] = useState({});
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdown
  const [currentErrors, setCurrentErrors] = useState({});
  const [categoryTypeOptions, setCategoryTypeOptions] = useState([]);
  const [stkGroupStatusOpt, setStkGroupStatusOpt] = useState([]);
  const [stkGroupActiveOpt, setStkGroupActiveOpt] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // tab ref
  const stakeholderGroupRef = useRef();

  //search ref
  const stakeholderGroupNameRef = useRef(null);
  const stakeholderGroupCodeRef = useRef(null);
  const stakeholderGroupCategoryRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const user = useSelector((state) => state.user);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setIsSearchLoading(true);

      //new

      const {
        active,
        status,
        stk_cat1: stkCat1Opt,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "stk_cat1" },
      ]);

      setActiveOptions(active);
      setStkGroupActiveOpt(active);
      setCategoryTypeOptions(stkCat1Opt);
      setStatusOptions(status);
      setStkGroupStatusOpt(status);

      setIsSearchLoading(false);
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditStkGroup(null);
    setDrawerTab(1);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    stakeholderGroupRef.current.resetForm();

    setEditMode(false);
    setEditStkGroup(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearStakeholderGroupError());

    const stakeholderGroupErrors = stakeholderGroupValidation(
      stakeholderGroupRef.current.getData()
    );
    if (Object.keys(stakeholderGroupErrors).length > 0) {
      dispatch(
        errorActions.validateStakeholderGroup({
          stakeholderGroupRequest: stakeholderGroupRef.current.getData(),
        })
      );
      setIsSubmitLoading(false);
      toast.error("There was an error adding Stakeholder Group", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    } else {
      if (editMode) {
        await updateStakeholderGroupData({
          stakeholderGroupRequest: stakeholderGroupRef.current.getData(),
          author: user.username,
        });
        toast.success("Stakeholder Group update successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        await addStakeholderGroupData({
          stakeholderGroupRequest: stakeholderGroupRef.current.getData(),
          author: user.username,
        });
        toast.success("Stakeholder Group added successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      setIsSubmitLoading(false);
      reset();
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!stakeholderGroupCategoryRef.current) {
      console.error("Stakeholder group category element is not available.");
      return;
    }
    if (tab === 1) {
      setIsLoading(false);
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const response = await searchStakeholderGroup({
      grpGroup: stakeholderGroupNameRef.current.value,
      grpCode: stakeholderGroupCodeRef.current.value,
      grpGroupCategory: stakeholderGroupCategoryRef.current.value,
      toDate: toDate,
      fromDate: fromDate,
      grpStatus: status,
      grpActive: active,
    });
    setSearchRes(response);

    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    stakeholderGroupNameRef.current.value = "";
    stakeholderGroupCodeRef.current.value = "";
    stakeholderGroupCategoryRef.current.value = "";
    setStatus(null);
    setActive(null);
    setFromDate("");
    setToDate("");
    setFormData({
      category: null,
      fromDate: null,
      toDate: null,
    });
  };

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Users size={15} style={{ marginRight: "5px" }} />
            </span>
            Stakeholder Group Search
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            onClick={handleDrawerOpen}
            authenticated={true}
            form={FORMS.STAKEHOLDER_GROUP.GRP.form}
            module={MODULES.Master}
            disabled={firstViableTab === -1}
            editMode={false}
          />
        </div>
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // handleReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui diving header">
                  Stakeholder Group Information
                </h4>
                <div className="four fields">
                  <div className="field">
                    <label>Stakeholder Group Name</label>
                    <input
                      type="text"
                      placeholder="Group Name"
                      name="stakeholderName"
                      ref={stakeholderGroupNameRef}
                    />
                  </div>
                  <div className="field">
                    <label>Stakeholder Group Code</label>
                    <input
                      type="text"
                      placeholder="Stakeholder Group Code"
                      name="stakeholderGroupCode"
                      ref={stakeholderGroupCodeRef}
                    />
                  </div>
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      name="stakeholderGroupExpiaryDate"
                      placeholder="To Date"
                      // ref={fromDateRef}
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      name="stakeholderGroupExpiaryDate"
                      placeholder="To Date"
                      // ref={toDateRef}
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="four fields">
                  <Dropdown
                    label="Category"
                    error={currentErrors.category}
                    module={MODULES.Master}
                    form={FORM}
                    field="grp_group_category"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { category: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    clearable
                    placeholder="Category"
                    style={{ height: "5px" }}
                    ref={stakeholderGroupCategoryRef}
                    options={categoryTypeOptions}
                    value={formData.category}
                    onChange={(e, data) => {
                      setFormData({
                        ...formData,
                        category: data.value,
                      });
                    }}
                  />
                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Id",
                    field: "grp_id",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Code",
                    field: "grpCode",
                  },
                  {
                    headerName: "Stakeholder Group Name",
                    field: "grpGroup",
                  },
                  {
                    headerName: "Category",
                    field: "grpGroupCategory",
                  },
                  {
                    headerName: "Status",
                    field: "grpStatus",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "grpActive",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  const response = await getStakeholderGroupDataById(
                    props.data.grp_id
                  );
                  setEditStkGroup(response);
                  setEditMode(true);
                  setIsDrawerOpen(true);
                  setSearchFetchLoading(false);
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <>
              <div
                className="stakeholder-code-header"
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>
                  Stakeholder Group Code:
                  {editStkGroup?.grpCode}
                </h3>
              </div>
            </>
          ) : null
        }
        body={
          <form className="drawer-content" onSubmit={handleSubmit}>
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={Users}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  errors={
                    Object.keys(errorState.stakeholderGroupErrors).length !== 0
                  }
                  label="Stakeholder Group"
                  authenticated={true}
                  form={FORMS.STAKEHOLDER_GROUP.GRP_DETAIL.form}
                  showMandatory={true}
                  isMandatory={true}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <StakeholderGroupComponent
                  ref={stakeholderGroupRef}
                  isLoading={isLoading}
                  errors={errorState.stakeholderGroupErrors}
                  dropdown={{
                    stkGroupStatusOpt,
                    categoryTypeOptions,
                    stkGroupActiveOpt,
                  }}
                  editMode={editMode}
                  currentValues={editMode ? editStkGroup : null}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {editMode ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.STAKEHOLDER_GROUP.GRP.form}
                      module={MODULES.Master}
                      section={FORMS.STAKEHOLDER_GROUP}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : drawerTab === 1 && !isLoading ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.STAKEHOLDER_GROUP.GRP.form}
                      module={MODULES.Master}
                      section={FORMS.STAKEHOLDER_GROUP}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </form>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};
export default StakeholderGroup;
